import React from 'react';
import { Box, useTheme, useMediaQuery, IconButton, Tooltip, CircularProgress } from '@mui/material';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highcharts3d from 'highcharts/highcharts-3d';
import { ChartTable } from '../tables/ChartTable';
import CustomDatePicker from '../customDatePicker';
import excelIcon from '../../assets/excel-icon.svg';
import {
  useGetPaymentTypeBreakdownQuery,
  PaymentTypeBreakdownResponse,
  useExportPaymentBreakdownMutation,
} from '@/services/dashboardApi';


// Initialize 3D module
highcharts3d(Highcharts);

const getPaymentData = (data: PaymentTypeBreakdownResponse, totalCount: number) => [
  {
    type: 'Net Banking',
    transactionCount: data?.NetBankingCount || 0,
    amount: data?.NetBankingAmount?.toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
    }),
    percentage:
      totalCount === 0
        ? '0%'
        : `${(((data?.NetBankingCount || 0) * 100) / totalCount).toFixed(2)}%`,
  },
  {
    type: 'Debit Card',
    transactionCount: data?.DebitCardCount || 0,
    amount: data?.DebitCardAmount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.DebitCardCount || 0) * 100) / totalCount).toFixed(2)}%`,
  },
  {
    type: 'Enach',
    transactionCount: data?.EnachCount || 0,
    amount: data?.EnachAmount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.EnachCount || 0) * 100) / totalCount).toFixed(2)}%`,
  },
  {
    type: 'UPI',
    transactionCount: data?.UPICount || 0,
    amount: data?.UPIAmount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.UPICount || 0) * 100) / totalCount).toFixed(2)}%`,
  },
  {
    type: 'UAUTO',
    transactionCount: data?.UAutoCount || 0,
    amount: data?.UAutoAmount?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }),
    percentage:
      totalCount === 0 ? '0%' : `${(((data?.UAutoCount || 0) * 100) / totalCount).toFixed(2)}%`,
  },
];

interface PaymentPieChartProps {
  mode?: 'date' | 'gateway'; // default will be 'date'
  onMenuItemSelect?: (item: string) => void;
  amcId?: string; // Optional AMC filter
}

export const PaymentPieChart: React.FC<PaymentPieChartProps> = ({ onMenuItemSelect, amcId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if screen is small
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(new Date());
  const [type, setType] = React.useState<'daily' | 'weekly' | 'monthly'>('daily');

  // Build API parameters with optional AMC filter
  const apiParams = React.useMemo(() => ({
    date: selectedDate?.toISOString() || new Date().toISOString(),
    type,
    amcId,
  }), [selectedDate, type, amcId]);

  const { data, isLoading } = useGetPaymentTypeBreakdownQuery(apiParams, {
    refetchOnMountOrArgChange: true,
  });

  const [exportPaymentBreakdown, { isLoading: isDownloading }] =
    useExportPaymentBreakdownMutation();

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const blob = await exportPaymentBreakdown(apiParams).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = `payment-breakdown-${apiParams.type}-${apiParams.date.split('T')[0]}.xlsx`;

      document.body.appendChild(link);

      // Use requestAnimationFrame to ensure proper download
      requestAnimationFrame(() => {
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };
  const totalCount =
    (data?.NetBankingCount || 0) +
    (data?.DebitCardCount || 0) +
    (data?.UPICount || 0) +
    (data?.EnachCount || 0) +
    (data?.UAutoCount || 0);
  const pieChartOptions: Highcharts.Options = {
    credits: { enabled: false },
    chart: {
      type: 'pie',
      options3d: { enabled: true, alpha: 45, beta: 0 },
      height: '300px',
    },
    title: {
      text: '',
      align: 'left',
      style: {
        ...theme.typography.h3,
        fontWeight: theme.typography.h3.fontWeight?.toString(),
      },
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      itemStyle: {
        fontSize: '12px',
      },
    },
    plotOptions: {
      pie: {
        innerSize: '30%',
        allowPointSelect: true,
        cursor: 'pointer',
        depth: 40,
        dataLabels: {
          enabled: true,
          alignTo: 'connectors',
          connectorShape: 'straight',
          distance: 20,
          formatter: function () {
            return `${this.point.name} ${this.point.y?.toFixed(2)}%`;
          },
          color: theme.palette.text.secondary,
        },
        showInLegend: true,
      },
    },
    series: [
      {
        type: 'pie',
        name: 'Amount',
        animation: false,
        data: [
          {
            name: 'Net Banking',
            y: isLoading
              ? 0
              : totalCount === 0
                ? 0
                : ((data?.NetBankingCount || 0) * 100) / totalCount,
            color: theme.palette.piechart.netBanking,
          },
          {
            name: 'Debit Card',
            y: isLoading
              ? 0
              : totalCount === 0
                ? 0
                : ((data?.DebitCardCount || 0) * 100) / totalCount,
            color: theme.palette.piechart.debitCard,
          },
          {
            name: 'UPI',
            y: isLoading ? 0 : totalCount === 0 ? 0 : ((data?.UPICount || 0) * 100) / totalCount,
            color: theme.palette.piechart.upi,
          },
          {
            name: 'Enach',
            y: isLoading ? 0 : totalCount === 0 ? 0 : ((data?.EnachCount || 0) * 100) / totalCount,
            color: theme.palette.piechart.enachmandates,
          },
          {
            name: 'UAUTO',
            y: isLoading ? 0 : totalCount === 0 ? 0 : ((data?.UAutoCount || 0) * 100) / totalCount,
            color: theme.palette.piechart.upimandates,
          },
        ],
      },
    ],
  };
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '10px',
        position: 'relative',
        width: '100%',
      }}
    >
      {/* Date Picker in Top-Right */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              ...theme.typography.h3,
              fontWeight: theme.typography.h3.fontWeight,
              p: 2.75,
            }}
          >
            Payment Type Breakdown
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Download Excel">
              <IconButton
                onClick={handleDownload}
                disabled={isDownloading || totalCount === 0}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  },
                  '&.Mui-disabled': {
                    color: theme.palette.action.disabled,
                  },
                }}
                aria-label="Export to Excel"
              >
                {isDownloading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <img src={excelIcon} alt="Export to Excel" style={{ width: 20, height: 20 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <CustomDatePicker
          value={selectedDate}
          disableFuture
          viewType={type === 'daily' ? 'day' : type === 'weekly' ? 'week' : 'month'}
          onChangeViewType={(newType) => {
            setType(newType === 'day' ? 'daily' : newType === 'week' ? 'weekly' : 'monthly');
            onMenuItemSelect?.(newType);
          }}
          onChange={(newValue) => {
            setSelectedDate(newValue);
            onMenuItemSelect?.(newValue?.toISOString() || '');
          }}
        />
      </Box>

      {/* Responsive Chart Container */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '600px',
          height: isMobile ? '300px' : '400px',
          mx: 'auto',
        }}
      >
        <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />
      </Box>

      {/* Responsive Table */}
      {!isLoading && data && <ChartTable data={getPaymentData(data, totalCount)} title="Payment Type" />}
    </Box>
  );
};
